import React, { useMemo } from 'react';
import { useJpa, useNonPcn, useRequest } from '@stores';
import GeneralProjectInformation from '@forms/input-forms/common/GeneralProjectInformation';
import NonPcnFormInstructions from '@forms/static-forms//NonPcnFormInstructions';
import Contacts from '@forms/input-forms/common/Contacts';
import NonPcnFormCertifySignSubmit from '@forms/input-forms/NonPcnForm/NonPcnCertifySignSubmit';
import NonPcnInformation from '@forms/input-forms/NonPcnForm/NonPcnInformation';
import SupportingInformation from '@forms/input-forms/common/SupportingInformation';
import JpaInfo from './JpaInfo';
import JpaForm, { generateJpaFormName } from './JpaForm';
import { PcnResultTypes, RequestType } from '@src/utils/enums';
import SelfVerifyOutcomePreview from '@forms/components/Form/SelfVerifyOutcomePreview';
import { nonPcnOutcomes } from '@pages/Forms/input-forms/NonPcnForm/nonPcnHelpers';

export const getNonPcnFormMetadata = () => {
  const baseFormArray = [
    NonPcnFormInstructions.metadata,
    Contacts.metadata,
    GeneralProjectInformation.metadata,
    NonPcnInformation.metadata,
    SupportingInformation.metadata,
    NonPcnFormCertifySignSubmit.metadata,
  ];

  const jpaBasicInfo = useJpa.getState().jpaBasicInfo;
  const isJpa = jpaBasicInfo?.length > 0;
  if (isJpa) {
    const jpaInfoSteps = [{ sectionName: 'Location Specific Information', isParentStep: true }];
    const jpaFormSteps = [{ sectionName: 'Location Specific Questions', isParentStep: true }];

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: 1,
      });

      jpaFormSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: jpaBasicInfo.length + baseFormArray.length - 1,
      });
    });

    baseFormArray.splice(1, 0, ...jpaInfoSteps);
    baseFormArray.splice(baseFormArray.length - 2, 0, ...jpaFormSteps);
  }

  return baseFormArray;
};

const generateSections = ({ setNonPcnRequest, isJpa, jpaBasicInfo, nonPcnData, nonPcnID }) => {
  const sections = [
    { id: 'NonPcnInstructions', name: 'Instructions', content: NonPcnFormInstructions },
    {
      id: 'NonPcnContacts',
      name: 'Contacts',
      content: Contacts,
      subtext: 'requesting confirmation that the proposed activity does not require authorization from the USACE.',
      componentID: nonPcnID,
    },
    { id: 'GeneralProjectInformation', name: 'General Project Information', content: GeneralProjectInformation },
    {
      id: 'NonPcnInformation',
      name: `${RequestType.NonPreconstructionNotification}`,
      content: NonPcnInformation,
      componentId: nonPcnID,
    },
    {
      id: 'SupportingInformation',
      name: 'Supporting Information',
      content: SupportingInformation,
      componentID: nonPcnID,
      subtext: (
        <p>
          Please upload any supporting documentation including information related to the request. Multiple documents of
          the same file type can be combined and uploaded as one document.
        </p>
      ),
      updateRequestData: setNonPcnRequest,
    },
    { id: 'NonPncCertifySignSubmit', name: 'Certify Sign Submit', content: NonPcnFormCertifySignSubmit },
  ];

  if (isJpa) {
    const jpaInfoSections = [{ id: 'jpa-info', name: 'Location Specific Information', content: () => <></> }];
    const jpaFormSections = [{ id: 'jpa-questions', name: 'Location Specific Questions', content: () => <></> }];

    const infoStartIndex = 1;
    const formStartIndex = sections.length + jpaBasicInfo.length - 1;
    const infoGroupIndexes = jpaBasicInfo.map((_, index) => index + infoStartIndex + 1);
    const formGroupIndexes = jpaBasicInfo.map((_, index) => index + formStartIndex + 1);

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSections.push({
        id: `jpa-info-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaInfo,
        componentId: nonPcnID,
        formId: jpa.formID,
        group: {
          indexes: infoGroupIndexes,
          parentIndex: infoStartIndex,
          parentName: jpaInfoSections[0].name,
        },
        requestId: nonPcnData.requestID,
        version: nonPcnData.version,
      });

      jpaFormSections.push({
        id: `jpa-questions-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaForm,
        componentId: nonPcnID,
        formId: jpa.formID,
        group: {
          indexes: formGroupIndexes,
          parentIndex: formStartIndex,
          parentName: jpaFormSections[0].name,
        },
        requestId: nonPcnData.requestID,
        version: nonPcnData.version,
      });
    });

    sections.splice(1, 0, ...jpaInfoSections);
    sections.splice(sections.length - 2, 0, ...jpaFormSections);
  }

  return sections;
};

const NonPcnForm = ({ formVersion }) => {
  const { setNonPcnRequest, nonPcnId, nonPcnData } = useNonPcn();
  const { jpaBasicInfo } = useJpa();
  const { getActiveSection } = useRequest();
  const isJpa = jpaBasicInfo?.length > 0;
  const activeStep = getActiveSection();

  const sections = useMemo(
    () =>
      generateSections({
        setNonPcnRequest,
        isJpa,
        jpaBasicInfo,
        nonPcnData: nonPcnData,
        nonPcnID: nonPcnId,
      }),
    [setNonPcnRequest, isJpa, jpaBasicInfo, nonPcnData, nonPcnId]
  );

  return (
    <>
      <SelfVerifyOutcomePreview
        outcomes={nonPcnOutcomes}
        defaultResult={PcnResultTypes.ContactDistrict}
        label='Preview Non-PCN outcome'
      />

      {sections.map((section, index) => {
        const Content = section.content;

        return (
          <section
            id={section.id}
            key={section.id}
            data-name={section.name}
            style={{ display: activeStep?.id === index ? 'inline' : 'none' }}
          >
            <Content stepNo={index} formVersion={formVersion} {...section} />
          </section>
        );
      })}
    </>
  );
};

export default NonPcnForm;
